import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CToast,
  CToastBody,
  CToastClose,
  CToaster,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../icons'

const UIContext = createContext(null)

export function useUI() {
  const ctx = useContext(UIContext)
  if (!ctx) {
    throw new Error('useUI debe usarse dentro de <UIProvider>')
  }
  return ctx
}

const TONES = {
  success: { variant: 'success', iconName: 'cilCheckCircle', dark: false },
  danger: { variant: 'danger', iconName: 'cilXCircle', dark: false },
  error: { variant: 'danger', iconName: 'cilXCircle', dark: false },
  warning: { variant: 'warning', iconName: 'cilWarning', dark: true },
  info: { variant: 'info', iconName: 'cilInfo', dark: true },
}

export function UIProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const [confirmState, setConfirmState] = useState(null)
  const [busy, setBusy] = useState(false)
  const idRef = useRef(0)
  const resolverRef = useRef(null)

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((item) => item.id !== id))
  }, [])

  const toast = useCallback((message, type = 'success', options = {}) => {
    const id = (idRef.current += 1)
    setToasts((list) => [
      ...list,
      {
        id,
        message,
        type,
        title: options.title || '',
        delay: typeof options.delay === 'number' ? options.delay : 4200,
      },
    ])
    return id
  }, [])

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve
      setConfirmState({
        title: options.title || 'Confirmar accion',
        body: options.body || 'Estas seguro de continuar?',
        confirmText: options.confirmText || 'Confirmar',
        cancelText: options.cancelText || 'Cancelar',
        tone: options.tone || 'danger',
        iconName: options.icon || (options.tone === 'success' ? 'cilCheckCircle' : 'cilWarning'),
      })
    })
  }, [])

  const settle = useCallback((result) => {
    const resolve = resolverRef.current
    resolverRef.current = null
    setConfirmState(null)
    setBusy(false)
    if (resolve) resolve(result)
  }, [])

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm])

  const confirmTone = confirmState ? TONES[confirmState.tone] || TONES.danger : TONES.danger

  return (
    <UIContext.Provider value={value}>
      {children}

      <CToaster className="ui-toaster" placement="top-end">
        {toasts.map((item) => {
          const tone = TONES[item.type] || TONES.info
          return (
            <CToast
              key={item.id}
              autohide
              visible
              delay={item.delay}
              color={tone.variant}
              className={`ui-toast ${tone.dark ? 'ui-toast--dark' : 'text-white'}`}
              onClose={() => dismiss(item.id)}
            >
              <div className="ui-toast-row">
                <CIcon icon={icon(tone.iconName)} className="ui-toast-icon" />
                <CToastBody className="ui-toast-body">
                  {item.title ? <strong className="ui-toast-title">{item.title}</strong> : null}
                  <span>{item.message}</span>
                </CToastBody>
                <CToastClose className="ui-toast-close m-auto" white={!tone.dark} />
              </div>
            </CToast>
          )
        })}
      </CToaster>

      <CModal
        visible={Boolean(confirmState)}
        onClose={() => settle(false)}
        alignment="center"
        className="ui-confirm"
      >
        {confirmState ? (
          <>
            <CModalHeader closeButton={false}>
              <CModalTitle className="ui-confirm-title">
                <span className={`ui-confirm-icon ui-confirm-icon--${confirmTone.variant}`}>
                  <CIcon icon={icon(confirmState.iconName)} />
                </span>
                {confirmState.title}
              </CModalTitle>
            </CModalHeader>
            <CModalBody className="ui-confirm-body">{confirmState.body}</CModalBody>
            <CModalFooter>
              <CButton color="light" onClick={() => settle(false)} disabled={busy}>
                {confirmState.cancelText}
              </CButton>
              <CButton
                color={confirmTone.variant}
                className={confirmTone.variant === 'success' ? 'btn-brand' : ''}
                onClick={() => {
                  setBusy(true)
                  settle(true)
                }}
                disabled={busy}
              >
                {confirmState.confirmText}
              </CButton>
            </CModalFooter>
          </>
        ) : null}
      </CModal>
    </UIContext.Provider>
  )
}

export default UIProvider
